import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import tw from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';

import { isValidPhoneNumber, TextArea, TextInput } from '@restworld/ui-ds';
import { useVariableOptions } from '@restworld/utils-common';
import { Picture, Variable } from '@restworld/utility-types';
import { PhoneInput, Select } from '@restworld/ui-ds-base';

import RestaurantOnboardingOpeningHours from './restaurant-opening-hours';
import RestaurantOnboardingImageGallery from './restaurant-onboarding-image-gallery';
import { useRestaurantOnboardingData } from './use-restaurant-onboarding-data-query';
import { isManualEntryAtom, manualAddressFieldsAtom } from '.';
import { ManualAddressInput } from '../../shared/manual-venue-entry';
import type { ManualAddressResult } from '../../shared/manual-venue-entry';

export type RestaurantOnboardingPayload = {
  restaurant_name: string;
  address: string;
  brief_description: string;
  covers_per_service: string;
  staff_size_floor: number;
  staff_size_kitchen: number;
  instagram_url: string;
  facebook_url: string;
  website_url: string;
  the_fork_url: string;
  opening_hours: string;
  pictures: Picture[];
  number_of_staffs: string;
  binary_pictures: File[];
  business_address: string;
  // Geo bundle co-located with `address`. Pre-filled from the GMaps selection
  // by `methods.reset({...restaurants})` in the non-manual flow, and rewritten
  // by `AddressField` when the employer re-picks an address suggestion, so
  // they always stay coherent with the displayed address.
  address_lat?: number;
  address_lon?: number;
  locality?: string;
  country?: string;
  postal_code?: string;
  administrative_area_level_1?: string;
  administrative_area_level_2?: string;
  // user contact information
  name: string;
  surname: string;
  email: string;
  phone_number: string;
  privacy_policy: boolean;
  terms_and_conditions: boolean;
  vexatious_clauses_acceptance: boolean;
};
export const coverPerServiceOptionsAtom = atom<Variable[]>([]);
export const useCoverPerServiceOptions = () => {
  const { variableOptions, fetchVariable } = useVariableOptions();
  const [coverPerServiceOptions, setCoverPerService] = useAtom(coverPerServiceOptionsAtom);

  const coverPerServicesData = variableOptions.covers_per_service;

  React.useEffect(() => {
    if (!coverPerServiceOptions?.length) fetchVariable(['covers_per_service']);
  }, [fetchVariable, coverPerServiceOptions?.length]);

  React.useEffect(() => {
    if (coverPerServicesData) setCoverPerService(coverPerServicesData);
  }, [setCoverPerService, coverPerServicesData]);
};
export const RestaurantOnboardingFormSection: React.FunctionComponent<{
  label: string;
  placeholder: string;
  name: keyof RestaurantOnboardingPayload;
  options?: Variable[];
  type?: 'text' | 'number' | 'url' | 'email' | 'textarea';
  required?: boolean | string;
}> = ({ label, placeholder, name, options, type = 'text', required }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<RestaurantOnboardingPayload>();
  const { t } = useTranslation('restaurant-onboarding');
  const formElement = React.useMemo(() => {
    if (name === 'phone_number')
      return (
        <Controller
          name="phone_number"
          control={control}
          // rules={{ required }}
          rules={{
            required,
            validate: (phoneNumber) => {
              if (!!phoneNumber && phoneNumber?.length >= 4)
                return isValidPhoneNumber(phoneNumber) || t('enter_valid_phone_number');
              // Checking the string length to know if the user has started enterting any number or not (by default, the country code is added to the value)
              else if (!!phoneNumber && phoneNumber?.length < 4) return t('enter_valid_phone_number');
            },
          }}
          render={({ field }) => (
            <>
              <PhoneInput
                {...field}
                id="phone_number"
                textInput={{
                  placeholder,
                  supportText: errors['phone_number']?.message,
                  supportTextVariant:
                    errors['phone_number']?.message && field?.value?.length >= 4 ? 'error' : 'default',
                  wrapperStyle: tw`border-neutral-100 border w-full`,
                  isFullwidth: true,
                }}
              />
              <p tw="text-neutral-300">*Ci serve per verificare che sia tu il vero gestore del locale</p>
            </>
          )}
        />
      );
    if (name === 'covers_per_service')
      return (
        <Controller
          name="covers_per_service"
          control={control}
          rules={{ required }}
          render={({ field }) => (
            <Select
              fullwidth
              options={
                options
                  ? options.map((v) => ({
                      value: v.id,
                      label: v.label,
                    }))
                  : []
              }
              placeholder="100"
              {...field}
              onChange={({ value }) => field.onChange(value)}
              value={{
                value: field.value,
                label: options ? options?.find((v) => v.id === field.value)?.label : '',
              }}
            />
          )}
        />
      );
    return (
      <Controller
        control={control}
        name={name}
        rules={{
          required,
          ...(type === 'url'
            ? {
                pattern: {
                  value: /^(https?:\/\/)([\w-]+\.)+[\w-]+(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/,
                  message: t('url_not_valid'),
                },
              }
            : type === 'email'
            ? {
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: t('enter_valid_email'),
                },
              }
            : {}),
        }}
        render={({ field }) =>
          name === 'brief_description' ? (
            <div tw="w-full">
              <TextArea
                id=""
                type="textarea"
                placeholder={placeholder}
                helperText={errors[name]?.message}
                color={Object.keys(errors).includes(name) ? 'failure' : undefined}
                twStyle={tw`min-w-full w-full`}
                rows={8}
                {...field}
                value={field.value?.toString() || ''} // Ensure value is always a string
              />
            </div>
          ) : (
            <TextInput
              {...field}
              value={typeof field.value === 'string' || typeof field.value === 'number' ? field.value : ''} // Ensure value is string or number
              placeholder={placeholder}
              id=""
              {...{ bordercolor: 'neutral-90', twStyle: tw`w-full` }}
              type={type}
              supportText={errors[name]?.message}
              isError={!!errors[name]}
            />
          )
        }
      />
    );
  }, [control, errors, name, options, placeholder, required, t, type]);
  return (
    <div tw="self-stretch inline-flex flex-col justify-start items-start gap-2">
      <h5 tw="justify-center text-neutral-700 text-base font-medium">{label}</h5>
      {formElement}
    </div>
  );
};

const FormHeader: React.FunctionComponent = () => {
  const { t } = useTranslation('restaurant-onboarding');
  const isManualEntry = useAtomValue(isManualEntryAtom);
  return (
    <div tw="self-stretch inline-flex flex-col justify-start items-start gap-4">
      <h5 tw="justify-start text-neutral-700 text-xl font-semibold">
        {t(isManualEntry ? 'enter_your_venue_details' : 'heres_what_we_found')}
      </h5>
      <h5 tw="self-stretch justify-start text-neutral-500 text-base font-normal">
        {t(isManualEntry ? 'enter_your_venue_details_subtitle' : 'heres_what_we_found_subtitle')}
      </h5>
    </div>
  );
};

// Address autocomplete used in BOTH onboarding flows:
// - manual entry (no GMaps Place selected) → persist the geocoded bundle into
//   `manualAddressFieldsAtom`, which `formatFormData` reads via `manualEntryGeoData`;
// - non-manual (post-GMaps selection) → write the geocoded bundle directly into
//   the form via setValue, overwriting the values the form was reset with from
//   the original GMaps fetch. `formatFormData` spreads those form values into
//   the payload, so editing the address re-syncs lat/lon/locality/etc.
//
// `gmaps_place_id` and `business_address` are intentionally left untouched in
// both flows (per product decision): the old Place id can stay attached, the
// authoritative geo signal is the new address the employer just picked.
const AddressField = () => {
  const { t } = useTranslation('restaurant-onboarding');
  const isManualEntry = useAtomValue(isManualEntryAtom);
  const {
    setValue,
    register,
    getValues,
    watch,
    formState: { errors },
  } = useFormContext<RestaurantOnboardingPayload>();
  const setManualAddressFields = useSetAtom(manualAddressFieldsAtom);

  React.useEffect(() => {
    register('address', { required: t('address_is_required') });
  }, [register, t]);

  // Reactive read so the autocomplete pre-fills with the GMaps-loaded address
  // when the form is reset (non-manual flow) and stays in sync with setValue.
  const currentAddress = watch('address') || getValues('address') || '';

  const handleAddressChange = React.useCallback(
    (value: string) => {
      setValue('address', value, { shouldValidate: true });
    },
    [setValue]
  );

  const handleAddressSelect = React.useCallback(
    (result: ManualAddressResult) => {
      setValue('address', result.addressString, { shouldValidate: true });

      if (isManualEntry) {
        setManualAddressFields({
          address_lat: result.lat,
          address_lon: result.lon,
          locality: result.locality,
          country: result.country,
          postal_code: result.postal_code,
          administrative_area_level_1: result.administrative_area_level_1,
          administrative_area_level_2: result.administrative_area_level_2,
        });
        return;
      }

      // Non-manual flow: write to the form. Skip empty strings (Google returns
      // "" for components it can't resolve) so we never overwrite a valid
      // pre-filled value with "".
      if (result.lat !== undefined) setValue('address_lat', result.lat);
      if (result.lon !== undefined) setValue('address_lon', result.lon);
      if (result.locality) setValue('locality', result.locality);
      if (result.country) setValue('country', result.country);
      if (result.postal_code) setValue('postal_code', result.postal_code);
      if (result.administrative_area_level_1)
        setValue('administrative_area_level_1', result.administrative_area_level_1);
      if (result.administrative_area_level_2)
        setValue('administrative_area_level_2', result.administrative_area_level_2);
    },
    [isManualEntry, setManualAddressFields, setValue]
  );

  return (
    <div tw="self-stretch inline-flex flex-col justify-start items-start gap-2">
      <h5 tw="justify-center text-neutral-700 text-base font-medium">{t('venue_address')}</h5>
      <ManualAddressInput
        formId="address-input"
        initialValue={currentAddress}
        onAddressChange={handleAddressChange}
        onAddressSelect={handleAddressSelect}
      />
      {errors.address?.message && <p tw="text-red-500 text-sm">{errors.address.message}</p>}
    </div>
  );
};

const RestaurantDescriptionForms = () => {
  const { t } = useTranslation('restaurant-onboarding');
  const isManualEntry = useAtomValue(isManualEntryAtom);
  return (
    <div tw="self-stretch inline-flex flex-col justify-start items-start gap-6 w-full">
      <RestaurantOnboardingFormSection
        label={t('name_of_the_venue')}
        placeholder="es. Il Mio Locale"
        name="restaurant_name"
        required={t('restaurant_name_is_required')}
      />
      <AddressField />
      <div tw="self-stretch flex flex-col gap-2 w-full">
        <RestaurantOnboardingFormSection
          label={t('description')}
          placeholder="es. Descrivi brevemente il tuo locale, la sua storia e cosa lo rende unico."
          name="brief_description"
          type="textarea"
          required={isManualEntry ? t('this_is_a_required_field') : undefined}
        />
        <p tw="text-sm text-neutral-200 leading-snug">{t('venue_description_lift_hint')}</p>
      </div>
    </div>
  );
};

const RestaurantStaffForms = () => {
  useCoverPerServiceOptions();
  const coverServices = useAtomValue(coverPerServiceOptionsAtom);
  const isManualEntry = useAtomValue(isManualEntryAtom);
  const { t } = useTranslation('restaurant-onboarding');
  return (
    <div tw="self-stretch inline-flex flex-col justify-start items-start gap-6 w-full">
      <RestaurantOnboardingFormSection
        label={isManualEntry ? `${t('staff_members')} (${t('optional')})` : t('staff_members')}
        placeholder="17"
        name="number_of_staffs"
        type="number"
      />
      <RestaurantOnboardingFormSection
        label={isManualEntry ? `${t('covers')} (${t('optional')})` : t('covers')}
        placeholder="100"
        name="covers_per_service"
        options={coverServices}
      />
    </div>
  );
};

const RestaurantLinksForm = () => {
  const restaurantData = useRestaurantOnboardingData();
  return (
    <div tw="self-stretch inline-flex flex-col justify-start items-start gap-6 w-full">
      {[
        ...(restaurantData?.website_url
          ? [{ label: 'Website', placeholder: 'https://www.example.com', name: 'website_url' }]
          : []),
        ...(restaurantData?.instagram_url
          ? [{ label: 'Instagram', placeholder: 'https://www.instagram.com/example', name: 'instagram_url' }]
          : []),
        ...(restaurantData?.facebook_url
          ? [{ label: 'Facebook', placeholder: 'https://www.facebook.com/example', name: 'facebook_url' }]
          : []),
        ...(restaurantData?.the_fork_url
          ? [{ label: 'The Fork', placeholder: 'https://www.thefork.com/example', name: 'the_fork_url' }]
          : []),
      ].map(({ label, placeholder, name }) => (
        <RestaurantOnboardingFormSection
          key={name}
          label={label}
          placeholder={placeholder}
          name={name as keyof RestaurantOnboardingPayload}
          type="url"
        />
      ))}
    </div>
  );
};
const RestaurantOnboardingForm: React.FunctionComponent = () => {
  const isManualEntry = useAtomValue(isManualEntryAtom);
  return (
    <div tw="flex flex-col gap-12 h-full w-full">
      <FormHeader />
      <RestaurantDescriptionForms />
      <hr />
      {!isManualEntry && (
        <>
          <RestaurantOnboardingOpeningHours />
          <hr />
        </>
      )}
      <RestaurantOnboardingImageGallery />
      <hr />
      <RestaurantStaffForms />
      {!isManualEntry && (
        <>
          <hr />
          <RestaurantLinksForm />
        </>
      )}
    </div>
  );
};

export default RestaurantOnboardingForm;
